import { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import GuruLayout from "../../components/layout/GuruLayout";
import {
  Calendar,
  BarChart3,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  HelpCircle, // Kita pakai HelpCircle di sini untuk tombol petunjuk dashboard
} from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API = "https://backend.smpn1kamal.sch.id/api";

export default function DashboardGuru() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [namaLengkap, setNamaLengkap] = useState("");
  const [jadwalHariIni, setJadwalHariIni] = useState([]);
  const [totalPresensi, setTotalPresensi] = useState(0);

  const [rekap, setRekap] = useState({
    hadir: 0,
    sakit: 0,
    izin: 0,
    alfa: 0,
  });

  // ✅ FETCH PROFIL + TOAST
  const fetchProfil = useCallback(async () => {
    const res = await axios.get(`${API}/profil-guru`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = res.data?.data || {};

    setNamaLengkap(data.nama_lengkap || "");

    // ✅ TAMBAHAN TOAST
    if (!data.nama_lengkap || !data.nip || !data.mapel) {
      toast.warning("Lengkapi profil anda terlebih dahulu", {
        position: "top-right",
        autoClose: 3000,
      });
    }

  }, [token]);

  const fetchJadwalHariIni = useCallback(async () => {
    const hari = new Date().toLocaleDateString("id-ID", {
      weekday: "long",
    });

    const res = await axios.get(`${API}/jadwal-guru`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { hari },
    });

    setJadwalHariIni(res.data?.data || []);
  }, [token]);

  const fetchRekap = useCallback(async () => {
    const res = await axios.get(`${API}/presensi/dashboard-guru`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = res.data || {};

    setRekap({
      hadir: Number(data.hadir) || 0,
      sakit: Number(data.sakit) || 0,
      izin: Number(data.izin) || 0,
      alfa: Number(data.alfa) || 0,
    });

    setTotalPresensi(Number(data.total) || 0);
  }, [token]);

  useEffect(() => {
    if (!token) return;

    Promise.all([
      fetchProfil(),
      fetchJadwalHariIni(),
      fetchRekap(),
    ]).catch(() => {});
  }, [token, fetchProfil, fetchJadwalHariIni, fetchRekap]);

  return (
    <GuruLayout>
      <div className="max-w-7xl mx-auto p-3 space-y-3">

        {/* CARD HI - BERSIH: KOMBISANI BIRU & KUNING TANPA UNGU */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl p-4 md:p-5 shadow-md transition-all duration-500 hover:shadow-xl hover:scale-[1.005] group">
          
          {/* Tombol Petunjuk di Pojok Kanan Atas Card */}
          <button 
            onClick={() => navigate("/guru/petunjuk")}
            className="absolute top-4 right-4 z-20 flex items-center gap-1.5 bg-white/15 hover:bg-white/25 border border-white/20 px-3 py-1.5 rounded-xl text-xs font-semibold backdrop-blur-sm transition-all duration-200 active:scale-95"
          >
            <HelpCircle size={14} />
            <span>Petunjuk</span>
          </button>

          {/* Elemen Dekoratif Aksen Kuning Terang di Background */}
          <div className="absolute -top-10 -left-10 w-28 h-28 bg-white/10 rounded-full blur-xl group-hover:scale-120 transition-transform duration-700 pointer-events-none" />
          <div className="absolute -right-4 -top-4 w-36 h-36 bg-yellow-300/20 rounded-full blur-2xl pointer-events-none transition-all duration-500 group-hover:bg-yellow-300/30" />
          <div className="absolute right-1/3 bottom-0 w-16 h-16 bg-yellow-300/10 rounded-full blur-xl pointer-events-none" />
          
          {/* Bayangan Ikon Buku Di Samping Kanan */}
          <div className="absolute -right-2 -bottom-8 text-white/10 pointer-events-none transform -rotate-12 group-hover:rotate-0 group-hover:scale-105 transition-all duration-500">
            <BookOpen size={110} strokeWidth={1} />
          </div>

          {/* Konten Utama Ringkas */}
          <div className="relative z-10 space-y-0.5">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight pr-24 flex items-center gap-2">
              Halo, {namaLengkap || "Guru"} <span className="animate-bounce">👋</span>
            </h1>
            <p className="text-xs md:text-sm text-blue-55/90 font-medium max-w-md leading-relaxed">
              Semoga harimu menyenangkan dan penuh semangat dalam mengajar!
            </p>
          </div>
        </div>

        {/* JADWAL */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2 text-slate-800">
              <Calendar size={20} className="text-blue-600" />
              Jadwal Hari Ini
            </h2>

            <button
              onClick={() => navigate("/guru/presensi")}
              className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-black px-4 py-2 rounded-full text-xs font-semibold shadow-md transition-all duration-300 hover:scale-105"
            >
              MULAI PRESENSI
            </button>
          </div>

          {jadwalHariIni.length === 0 ? (
            <p className="text-sm text-gray-500">Tidak ada jadwal hari ini</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {jadwalHariIni.map((j) => (
                <li key={j.id_jadwal} className="flex justify-between border-b pb-1">
                  <span>
                    {j.jam_mulai} - {j.jam_selesai}
                  </span>
                  <span className="font-medium">
                    {j.nama_mapel} - {j.nama_kelas}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* STAT */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard title="Hadir" value={rekap.hadir} icon={<CheckCircle className="text-green-600" />} />
          <StatCard title="Sakit" value={rekap.sakit} icon={<AlertCircle className="text-yellow-500" />} />
          <StatCard title="Izin" value={rekap.izin} icon={<Users className="text-blue-500" />} />
          <StatCard title="Alfa" value={rekap.alfa} icon={<XCircle className="text-red-500" />} />
        </div>

        {/* CHART + KALENDER */}
        <div className="grid md:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl shadow p-4 md:col-span-2">
            <h2 className="font-semibold mb-3 flex items-center gap-2 text-slate-800">
              <BarChart3 size={20} className="text-blue-600" />
              Laporan / Hari ini
            </h2>

            <div className="w-full h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={[{
                    time: "Hari Ini",
                    hadir: rekap.hadir,
                    izin: rekap.izin,
                    sakit: rekap.sakit,
                    alfa: rekap.alfa,
                  }]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />

                  <Line type="monotone" dataKey="hadir" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="izin" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="sakit" stroke="#f97316" strokeWidth={2} />
                  <Line type="monotone" dataKey="alfa" stroke="#ef4444" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <CalendarGuru />
        </div>
      </div>
    </GuruLayout>
  );
}

/* CALENDAR */
function CalendarGuru() {
  const year = new Date().getFullYear();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());

  const monthNames = [
    "Januari","Februari","Maret","April",
    "Mei","Juni","Juli","Agustus",
    "September","Oktober","November","Desember"
  ];

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = new Date(year, month, 1).getDay();

  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  return (
    <div className="bg-white rounded-xl shadow p-3">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setMonth((m) => Math.max(0, m - 1))}
          className="p-1 rounded-full hover:bg-slate-100">
          <ChevronLeft size={18} />
        </button>

        <h2 className="text-sm font-semibold text-slate-700">
          {monthNames[month]} {year}
        </h2>

        <button onClick={() => setMonth((m) => Math.min(11, m + 1))}
          className="p-1 rounded-full hover:bg-slate-100">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 text-xs text-center text-slate-500 mb-1">
        {["Min","Sen","Sel","Rab","Kam","Jum","Sab"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 text-sm">
        {days.map((day, i) => {
          const isToday =
            day &&
            today.getDate() === day &&
            today.getMonth() === month;

          return (
            <div
              key={i}
              className={`h-9 flex items-center justify-center rounded
                ${day ? "hover:bg-slate-100" : ""}
                ${isToday ? "bg-blue-600 text-white font-semibold rounded-full" : ""}
              `}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* STAT CARD */
function StatCard({ title, value, icon }) {
  return (
    <div className="bg-white rounded-xl shadow p-3 flex items-center gap-3">
      <div className="p-2 bg-gray-100 rounded-full">{icon}</div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-xl font-bold">{value}</p>
      </div>
    </div>
  );
}